import { ColumnsType } from 'antd/es/table';
import { ColumnSelector } from 'components/common/Column/ColumnSelector';
import ExcelOperationsHandler from 'components/common/ExcelOperationsHandler/ExcelOperationsHandler';
import ViewStaffModal from 'components/common/Modal/Staff/ViewStaffModal';
import TableCommon from 'components/common/Table/TableCommon';
import { useWareHouseCardOperations } from 'hooks/useWareHouseCardOperations';
import { Key, useState } from 'react';
import { Link } from 'react-router-dom';
import { Columns } from 'types';
import { WarehouseCardProductRecord } from 'types/ware-house-card-product.type';
import { formatPrice } from 'utils';
import { parseSimpleFilter } from 'utils/filterBuilder';

const WarehouseCardProduct = () => {
  const referenceTypeLabel: Record<WarehouseCardProductRecord['referenceType'], string> = {
    IMPORT: 'Nhập kho',
    EXPORT: 'Xuất kho',
    ADJUSTMENT: 'Điều chỉnh',
    TRANSFER: 'Chuyển kho',
  };
  const referenceTypeClassName: Record<WarehouseCardProductRecord['referenceType'], string> = {
    IMPORT: 'text-green-600 bg-green-50',
    EXPORT: 'text-red-500 bg-red-50',
    ADJUSTMENT: 'text-orange-500 bg-orange-50',
    TRANSFER: 'text-blue-600 bg-blue-50',
  };
  const renderMovementValue = (value: number | string | undefined, className: string) => {
    if (value === null || value === undefined || value === '') {
      return <span className='text-[rgba(0,0,0,0.45)] font-[600]'>---</span>;
    }

    const normalizedValue = typeof value === 'string' ? Number(value.replace(/,/g, '').trim()) : Number(value);

    if (!Number.isFinite(normalizedValue) || normalizedValue === 0) {
      return <span className='text-[rgba(0,0,0,0.45)] font-[600]'>---</span>;
    }

    return <span className={`font-[600] ${className}`}>{value.toLocaleString()}</span>;
  };

  const {
    tableState,
    filteredQuery,
    handlePagination,
    warehouseCardProductData,
    warehouseCardProductLoading,
    wareHouseData,
  } = useWareHouseCardOperations();
  const [staff, setStaff] = useState<unknown>(null);
  const [isOpenAddModal, setIsOpenAddModal] = useState<boolean>(false);
  const warehouseCardProductColumns: ColumnsType<WarehouseCardProductRecord> = [
    {
      title: 'Ngày tháng',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      sorter: (a, b) => new Date(a.transactionDateRaw).getTime() - new Date(b.transactionDateRaw).getTime(),
    },
    {
      title: 'Số hiệu chứng từ',
      dataIndex: 'refCode',
      key: 'refCode',
      render: (value, record) => {
        return (
          <p className='font-[600] text-[var(--primaryColor500)]'>
            {value ? (
              <>
                <Link
                  className='cursor-pointer'
                  to={
                    record?.referenceType === 'IMPORT'
                      ? `/management/inventory/purchase-orders/importing/${record?.refId}`
                      : `/management/inventory/out-orders/${record?.refId}`
                  }
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={(e) => e.stopPropagation()}
                >
                  {value || '---'}
                </Link>
              </>
            ) : (
              <>---</>
            )}
          </p>
        );
      },
      sorter: (a, b) => (a.refCode || '').localeCompare(b.refCode || ''),
    },
    {
      title: 'Diễn giải',
      dataIndex: 'referenceType',
      key: 'referenceType',
      width: 140,
      render: (value: WarehouseCardProductRecord['referenceType'] | undefined) => {
        return (
          <div>
            {value ? (
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-[13px] font-[600] ${referenceTypeClassName[value] ?? 'text-[var(--primaryColor500)] bg-[color-mix(in_srgb,var(--primaryColor500)_10%,white)]'}`}
              >
                {referenceTypeLabel[value] ?? '---'}
              </span>
            ) : (
              <span className='text-[rgba(0,0,0,0.45)] font-[600]'>---</span>
            )}
          </div>
        );
      },
      sorter: (a, b) => (a.referenceType || '').localeCompare(b.referenceType || ''),
    },
    {
      title: 'Đơn giá (VNĐ)',
      dataIndex: 'costPrice',
      key: 'costPrice',
      align: 'right',
      filterSearch: false,
      sorter: (a, b) => Number(a.costPrice) - Number(b.costPrice),
    },
    {
      title: 'Nhập',
      dataIndex: 'quantityIn',
      key: 'quantityIn',
      align: 'right',
      filterSearch: false,
      render: (value) => renderMovementValue(value, 'text-green-600'),
      sorter: (a, b) => Number(a.quantityIn) - Number(b.quantityIn),
    },
    {
      title: 'Xuất',
      dataIndex: 'quantityOut',
      key: 'quantityOut',
      align: 'right',
      filterSearch: false,
      render: (value) => renderMovementValue(value, 'text-red-500'),
      sorter: (a, b) => Number(a.quantityOut) - Number(b.quantityOut),
    },
    {
      title: 'Tồn',
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      align: 'right',
      filterSearch: false,
      render: (value) => value?.toLocaleString(),
      sorter: (a, b) => Number(a.balanceAfter) - Number(b.balanceAfter),
    },
    {
      title: 'Nhân viên',
      dataIndex: 'staffNameCode',
      key: 'staffNameCode',
      filterSearch: false,
      render: (_, record) => {
        return (
          <p className='font-[600] text-[var(--primaryColor500)]'>
            {record?.staff?.baseUser ? (
              <>
                <div
                  onClick={() => {
                    setStaff(record.staff);
                    setIsOpenAddModal(true);
                  }}
                  className='cursor-pointer'
                >
                  <p>
                    {record?.staff?.baseUser?.code || ''} - {record?.staff?.baseUser?.name || ''}
                  </p>
                </div>
              </>
            ) : (
              <>---</>
            )}
          </p>
        );
      },
      sorter: (a, b) => (a.staff?.baseUser?.code || '').localeCompare(b.staff?.baseUser?.code || ''),
    },
  ];

  const warehouseCardProductColumnsCheck: Columns[] = warehouseCardProductColumns.map((column) => ({
    key: column.key as string,
    title: column.title as string,
  }));
  const [checkedList, setCheckedList] = useState<(Key | undefined)[]>(
    warehouseCardProductColumns.map((column) => column.key),
  );
  const getFilteredColumns = () => {
    return warehouseCardProductColumns.map((item) => ({
      ...item,
      hidden: !checkedList.includes(item.key),
    }));
  };
  return (
    <div className='flex flex-col h-full relative py-2'>
      <div className='flex-1 flex justify-end'>
        <div className='mb-3 flex items-center gap-3'>
          <div className='flex items-center gap-3'>
            <div className='font-medium text-[14px] text-[rgba(0,0,0,0.56)]'>Tổng giá trị nhập:</div>
            <div className='w-[180px] h-[40px] flex items-center justify-center rounded-[8px] border bg-white text-green-600 font-semibold border-rad'>
              {formatPrice(warehouseCardProductData?.data?.kpis?.totalImport || 0)} VND
            </div>

            <div className='font-medium text-[14px] text-[rgba(0,0,0,0.56)]'>Tổng giá trị xuất:</div>
            <div className='w-[180px] h-[40px] flex items-center justify-center rounded-[8px] border bg-white text-red-500 font-semibold'>
              {formatPrice(warehouseCardProductData?.data?.kpis?.totalExport || 0)} VND
            </div>
            <div className='font-medium text-[14px] text-[rgba(0,0,0,0.56)]'>Tổng giá trị tồn:</div>
            <div className='w-[180px] h-[40px] flex items-center justify-center rounded-[8px] border bg-white text-red-500 font-semibold'>
              {formatPrice(warehouseCardProductData?.data?.kpis?.totalRemain || 0)} VND
            </div>
          </div>
        </div>
      </div>

      <div className='w-full bg-[var(--textWhite)]'>
        <div className='flex justify-between items-center px-[24px] py-[12px]'>
          <h1 className='text-[18px] leading-[26px] font-[700]'>DANH SÁCH THẺ KHO</h1>
          <div className='flex gap-2'>
            <ColumnSelector
              allColumns={warehouseCardProductColumnsCheck}
              selectedColumns={checkedList}
              onChange={(selected) => setCheckedList(selected)}
            />

            <ExcelOperationsHandler
              entityType='warehouse-card'
              entityName='Thẻ kho'
              //   customHandlers={exportHandlers}
              //  disabled={isPerformingOperation}
            />
          </div>
        </div>
      </div>

      <div className='flex-grow bg-[var(--textWhite)] flex flex-col'>
        <div className='flex-grow flex flex-col'>
          <TableCommon<WarehouseCardProductRecord>
            columns={getFilteredColumns()}
            dataSource={wareHouseData}
            defaultPageSize={filteredQuery.size as number}
            count={warehouseCardProductData?.data?.count || 0}
            // onDelete={handleDelete}
            // onEdit={(record: WarehouseCardProductRecord) => {
            //   navigate(`/management/customers/tabs/${record.id}?query=TTCB`);
            // }}
            tableProps={{
              scroll: { x: 'max-content' },
              size: 'large',
              className: 'h-[calc(100vh - 290px)]',
              style: {
                minHeight: `calc(100vh - 200px') `,
                display: 'flex',
                flexDirection: 'column',
              },
              bordered: true,
              loading: warehouseCardProductLoading,
            }}
            onPaginationChange={handlePagination}
            currentPage={Number(filteredQuery.page)}
            searchColumnDefault={parseSimpleFilter(tableState?.filter)}
            actions={{
              edit: false,
              delete: false,
              copy: false,
            }}
          />
        </div>
      </div>
      <ViewStaffModal
        isOpen={isOpenAddModal}
        onCancel={() => setIsOpenAddModal(false)}
        staffData={
          staff as {
            baseUser: {
              code: string;
              name: string;
              email?: string;
              phoneNumber?: string;
              isActive?: boolean;
            };
          }
        }
      />
    </div>
  );
};

export default WarehouseCardProduct;
